import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import * as UUID from 'uuid'
import BaseTagPicker, { BaseTagPickerProps } from './BaseTagPicker'
import useWorkflows from '../../hooks/useWorkflows'
import { FileWorkflowInstanceDto } from '../../config/fileConfig'
import { workflowInstanceLabel } from '../../helpers/workflowHelper'

export type WorkflowDto = {
    id: string
    name: string
    enabled: boolean
}

/**
 * The picker works on a synthetic entity list (HAM-4121): one entity per attached workflow
 * instance (rendered as a dismissible chip, never shown in the dropdown) plus one 'add' entity
 * per enabled workflow (shown in the dropdown, never selected — so the same workflow stays
 * selectable any number of times).
 */
type WorkflowEntity = {
    key: string
    label: string
    workflow: WorkflowDto
    instance?: FileWorkflowInstanceDto
}

const ADD_PREFIX = 'add:'

// The workflowId fallback covers scaffold-provided instances (no id, no clientKey yet); the
// scaffold never contains duplicates, so it is unique there.
export const instanceKey = (instance: FileWorkflowInstanceDto): string => instance.id ?? instance.clientKey ?? instance.workflowId

export type WorkflowPickerProps = Partial<Omit<BaseTagPickerProps<WorkflowEntity>, 'entities' | 'selectedOptions' | 'onOptionsSelect'>> & {
    instances: FileWorkflowInstanceDto[]
    onInstancesChange: (e: Event | React.SyntheticEvent, instances: FileWorkflowInstanceDto[]) => void
}

export default function WorkflowPicker(props: WorkflowPickerProps): ReactElement {
    const { workflows } = useWorkflows()
    const { instances, onInstancesChange, ...pickerProps } = props

    const entities: WorkflowEntity[] = useMemo(() => {
        // Unsaved instances have no sequence number yet; display a provisional max+1. The server
        // assigns the real number (which counts removed instances too) on save.
        const maxByWorkflow = new Map<string, number>()
        instances.forEach(instance =>
            maxByWorkflow.set(instance.workflowId, Math.max(maxByWorkflow.get(instance.workflowId) || 0, instance.sequenceNumber || 0)),
        )
        const displayNumber = (instance: FileWorkflowInstanceDto): number => {
            if (instance.sequenceNumber) {
                return instance.sequenceNumber
            }
            const next = (maxByWorkflow.get(instance.workflowId) || 0) + 1
            maxByWorkflow.set(instance.workflowId, next)
            return next
        }

        const instanceEntities: WorkflowEntity[] = instances
            .map(instance => ({ instance, workflow: workflows.find(workflow => workflow.id === instance.workflowId) }))
            .filter(({ workflow }) => !!workflow)
            .map(({ instance, workflow }) => {
                const siblings = instances.filter(sibling => sibling.workflowId === instance.workflowId).length
                return {
                    key: instanceKey(instance),
                    label: workflowInstanceLabel(workflow.name, displayNumber(instance), siblings),
                    workflow,
                    instance,
                }
            })

        const addEntities: WorkflowEntity[] = workflows.map(workflow => ({
            key: ADD_PREFIX + workflow.id,
            label: workflow.name,
            workflow,
        }))

        return [...instanceEntities, ...addEntities]
    }, [workflows, instances])

    return (
        <BaseTagPicker<WorkflowEntity>
            alphabetical
            placeholderKey={'base.workflowpicker.placeholder'}
            {...pickerProps}
            entities={entities}
            filter={entity => !!entity.instance || entity.workflow.enabled}
            getEntityLabel={entity => entity.label}
            getEntityValue={entity => entity.key}
            selectedOptions={instances.map(instanceKey)}
            onOptionsSelect={(e, selectedOptions) => {
                const kept = instances.filter(instance => selectedOptions.includes(instanceKey(instance)))
                const added = selectedOptions
                    .filter(option => option.startsWith(ADD_PREFIX))
                    .map(option => option.substring(ADD_PREFIX.length))
                    .map(workflowId => ({ workflowId, clientKey: UUID.v4() }))
                onInstancesChange(e, [...kept, ...added])
            }}
        />
    )
}
